/**
 * LeftShield Admin - Login Page
 *
 * Landing page for unauthenticated users with Azure AD sign-in.
 */
import React from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title1,
  Body1,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { ShieldCheckmarkRegular, PersonRegular } from '@fluentui/react-icons';
import { useAuth } from '../hooks/useAuth';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    width: '400px',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXXL,
    fontSize: '2rem',
    color: tokens.colorBrandForeground1,
  },
  title: {
    marginBottom: tokens.spacingVerticalM,
  },
  description: {
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalXXL,
  },
  button: {
    width: '100%',
    marginBottom: tokens.spacingVerticalM,
  },
  footer: {
    marginTop: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

export const LoginPage: React.FC = () => {
  const styles = useStyles();
  const { signIn, isLoading } = useAuth();
  const [error, setError] = React.useState<string | null>(null);

  const handleSignIn = async () => {
    try {
      setError(null);
      await signIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    }
  };

  return (
    <div className={styles.root}>
      <Card className={styles.card}>
        <div className={styles.logo}>
          <ShieldCheckmarkRegular />
          <span>LeftShield</span>
        </div>

        <Title1 className={styles.title}>Platform Admin</Title1>

        <Body1 className={styles.description}>
          Sign in with your Transformr account to manage customer tenants,
          trials, and platform operations.
        </Body1>

        {isLoading ? (
          <Spinner size="medium" label="Signing in..." />
        ) : (
          <Button
            appearance="primary"
            size="large"
            icon={<PersonRegular />}
            className={styles.button}
            onClick={handleSignIn}
          >
            Sign in with Microsoft
          </Button>
        )}

        {error && (
          <Body1 style={{ color: tokens.colorPaletteRedForeground1, marginTop: tokens.spacingVerticalM }}>
            {error}
          </Body1>
        )}

        <Body1 className={styles.footer}>
          For Transformr staff only. Unauthorized access is prohibited.
        </Body1>
      </Card>
    </div>
  );
};

export default LoginPage;
